import { z } from 'zod';
import type {
  ParcelCreateProductDraft,
  ParcelCreateProductDraftErrors,
} from '@/features/parcel-create-products/modules/types';
import type { ApiParcelsProductsPostBody } from '@/shared/types/api/parcels-products';

// Mirrors the schema used by the parcel-products dialog. Inlined here to
// keep the inline-declaration feature free of cross-feature imports per
// the FRONTEND_RULES layer-ordering.
const productSchema = z.object({
  category_id: z.string().min(1, 'parcels.products.errors.categoryRequired'),
  subcategory_id: z.string().min(1, 'parcels.products.errors.subcategoryRequired'),
  full_name: z
    .string()
    .trim()
    .min(2, 'parcels.products.errors.fullNameRequired')
    .max(200, 'parcels.products.errors.tooLong'),
  quantity: z.coerce
    .number({ message: 'parcels.products.errors.quantityInvalid' })
    .int('parcels.products.errors.quantityInvalid')
    .min(1, 'parcels.products.errors.quantityMin'),
  price_per_item: z.coerce
    .number({ message: 'parcels.products.errors.priceInvalid' })
    .gt(0, 'parcels.products.errors.priceMin'),
});

export type DraftValidationResult =
  | { ok: true; body: ApiParcelsProductsPostBody }
  | { ok: false; errors: ParcelCreateProductDraftErrors };

// Run a single draft row through the existing parcel-products zod schema.
// On success returns the POST body shape; on failure returns per-field
// error keys (which the UI maps to i18n strings).
export const validateDraft = (draft: ParcelCreateProductDraft): DraftValidationResult => {
  const parsed = productSchema.safeParse({
    category_id: draft.category_id,
    subcategory_id: draft.subcategory_id,
    full_name: draft.full_name,
    quantity: draft.quantity,
    price_per_item: draft.price_per_item,
  });
  if (parsed.success) {
    return { ok: true, body: parsed.data };
  }
  const errors: ParcelCreateProductDraftErrors = {};
  for (const issue of parsed.error.issues) {
    const key = issue.path[0];
    if (
      key === 'category_id' ||
      key === 'subcategory_id' ||
      key === 'full_name' ||
      key === 'quantity' ||
      key === 'price_per_item'
    ) {
      if (!errors[key]) errors[key] = issue.message;
    }
  }
  return { ok: false, errors };
};

// Re-check rows that currently show errors after the user edits them, so each
// red border clears as soon as its field becomes valid. Only fields that were
// already flagged stay flagged — untouched fields never gain new errors here
// (full validation happens again on submit).
export const revalidateDraftErrors = (
  prev: Record<string, ParcelCreateProductDraftErrors>,
  drafts: ParcelCreateProductDraft[],
): Record<string, ParcelCreateProductDraftErrors> => {
  const next: Record<string, ParcelCreateProductDraftErrors> = {};
  for (const draft of drafts) {
    const prevErrors = prev[draft.client_id];
    if (!prevErrors) continue;
    const result = validateDraft(draft);
    if (result.ok) continue;
    const kept: ParcelCreateProductDraftErrors = {};
    for (const key of Object.keys(prevErrors) as (keyof ParcelCreateProductDraftErrors)[]) {
      if (result.errors[key]) kept[key] = result.errors[key];
    }
    if (Object.keys(kept).length > 0) next[draft.client_id] = kept;
  }
  return next;
};
